import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	Dispatch,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ContactExtendedSearchRequestModel,
	ContactGlobalSearchResultItem,
	ContactSearchSource,
	HighlightItem,
	GlobalSearchResultItemKind,
	UserSortBy,
	searchContacts,
	SearchIntegrationKind,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	LazyLoadCursorKind,
	RemoteDataSourceState,
	createRemoteDataSource,
	useAlerts,
	useSetStateAction,
	useValueEqualMemo
} from "client/Hooks";
import {
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	MultiSelectOption
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	ContactListItem,
	ContactSource,
	listWorkspaceContactsDepartments,
	listWorkspaceContactsJobTitles,
	listWorkspaceContactsOfficeLocations
} from "client/Api/Email/contacts";
import { ContactDialog } from "../../AddressBook/ContactDialog";
import {
	ContactGroupingKind,
	IContactGroup,
	groupAlreadySortedContacts
} from "../../AddressBook/Overview/List/groups";
import { findLastIndex } from "client_legacy/ndaw/arrayHelpers";
import styles from "./ContactSearch.module.less";
import { groupByToEmptyGroupTitle } from "../../AddressBook/Overview/List/List";
import classNames from "classnames";
import { AddressBookContactSource } from "../../AddressBook/Overview/Overview";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { SearchNoResults } from "../SearchNoResults";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { ConfigurableAddressBookAppContactSource } from "client/Api/Apps/EditApp/saveApp";
import { checkHasFilter } from "./common";
import { ContactItemView } from "../Items/ContactItemView";
import { never } from "client_legacy/ndaw/errors";
import { IValueInputAttachable } from "client/Forms/attachable";

export type ContactSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const ContactSearch = (props: ContactSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const {
		isAppAvailable,
		configurableContactSources
	 } = userConfiguration().addressBook;

	if (!isAppAvailable) {
		throw new Error("Contact search is disabled because the address book app is not available.");
	}

	const defaultSource: ContactSearchSource.Workspace | ContactSearchSource.All = configurableContactSources.length === 0
		? ContactSearchSource.Workspace
		: ContactSearchSource.All;

	const [source, setSource] = useState<ContactSearchSource>(defaultSource);

	const sourceOptions = useMemo(
		(): readonly SelectOption<ContactSearchSource>[] => {
			return [
				{
					text: localize("_AllContacts"),
					value: ContactSearchSource.All
				},
				{
					text: localize("_Workspace(Contacts)"),
					value: ContactSearchSource.Workspace
				},
				...configurableContactSources.map(
					x => configurableContactSourcesToContactSearchSourceItem[x]
				)
			];
		},
		[configurableContactSources]
	);

	switch (source) {
		case ContactSearchSource.All:
		case ContactSearchSource.Personal:
		case ContactSearchSource.Workspace:
			return (
				<DefaultContent
					initialSource={source}
					searchTerm={searchTerm}
					defaultSource={defaultSource}
					mobileSearchSettingsTarget={mobileSearchSettingsTarget}
					onSourceChange={setSource}
					sourceOptions={sourceOptions}
				/>
			);
		case ContactSearchSource.Organisation:
			return (
				<GalContent
					searchTerm={searchTerm}
					defaultSource={defaultSource}
					mobileSearchSettingsTarget={mobileSearchSettingsTarget}
					onSourceChange={setSource}
					sourceOptions={sourceOptions}
				/>
			);
		default:
			never(source);
	}
};
ContactSearch.displayName = "ContactSearch";

type DefaultContentProps = {
	searchTerm: VersionedValue<string>;
	initialSource: Exclude<ContactSearchSource, ContactSearchSource.Organisation>;
	defaultSource: ContactSearchSource.Workspace | ContactSearchSource.All;
	sourceOptions: readonly SelectOption<ContactSearchSource>[];
	mobileSearchSettingsTarget: HTMLDivElement | null;
	onSourceChange: (value: ContactSearchSource) => void;
};

const DefaultContent = (props: DefaultContentProps): ReactElement => {
	const {
		searchTerm,
		initialSource,
		defaultSource,
		sourceOptions,
		mobileSearchSettingsTarget,
		onSourceChange
	} = props;

	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const defaultFilters = useMemo(
		(): FilterModel => getDefaultFilters(defaultSource),
		[defaultSource]
	);

	const [formData, setFormData] = useState<ContactSearchFormData>({
		...defaultFilters,
		sort: defaultSortValue,
		source: initialSource
	});

	const [jobTitleOptions, setJobTitleOptions] =
		useState<readonly MultiSelectOption<string>[]>(EMPTY_ARRAY);
	const [departmentOptions, setDepartmentOptions] =
		useState<readonly MultiSelectOption<string>[]>(EMPTY_ARRAY);
	const [officeLocationOptions, setOfficeLocationOptions] =
		useState<readonly MultiSelectOption<string>[]>(EMPTY_ARRAY);

	const alertManager: AlertManager = useAlerts();
	const contentRef: RefObject<ContentHandle | null> = useRef<ContentHandle | null>(null);

	const onChange = useCallback(
		(value: ContactSearchFormData): void => {
			setFormData(
				prevState => {
					if (prevState.source !== value.source) {
						return {
							...defaultFilters,
							sort: defaultSortValue,
							source: value.source
						};
					}

					return value;
				}
			);

			onSourceChange(value.source);
		},
		[defaultFilters, onSourceChange]
	);

	const setValue: Dispatch<SetStateAction<ContactSearchFormData>> = useSetStateAction(
		formData,
		onChange
	);

	const formHandler: IFormHandler<ContactSearchFormData> = useFormHandler(
		formData,
		setValue,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingFilters(true);

					const officeLocationsRequest: Promise<readonly string[]> = listWorkspaceContactsOfficeLocations(signal);
					const departmentsRequest: Promise<readonly string[]> = listWorkspaceContactsDepartments(signal);
					const jobTitlesRequest: Promise<readonly string[]> = listWorkspaceContactsJobTitles(signal);

					const [
						officeLocations,
						departments,
						jobTitles
					] = await Promise.all([
						officeLocationsRequest,
						departmentsRequest,
						jobTitlesRequest
					]);

					setOfficeLocationOptions(convertToSortedOptions(officeLocations));
					setDepartmentOptions(convertToSortedOptions(departments));
					setJobTitleOptions(convertToSortedOptions(jobTitles));

					setIsLoadingFilters(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingFilters(false);
					console.error(e);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const clearHandler = useCallback(
		(): void => {
			formHandler
				.reducer
				.setValue({
					...formHandler.value,
					...defaultFilters,
					sort: defaultSortValue
				});

			formHandler
				.validation
				.triggerValueChanged();
		},
		[
			defaultFilters,
			formHandler
		]
	);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				source: formData.source,
				departments: formData.departments,
				jobTitles: formData.jobTitles,
				officeLocations: formData.officeLocations,
				department: formData.department.value,
				jobTitle: formData.jobTitle.value,
				officeLocation: formData.officeLocation.value
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			searchTerm,
			formData
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems: formData.source === ContactSearchSource.All
			? DEFAULT_SIZE_FOR_ALL_SOURCE
			: desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			contentRef
				.current
				?.resetScroll();
		},
		[partialDataSourceOptions]
	);

	const shouldShowFilters: boolean = CONTACT_SOURCES_WITH_FILTERS.includes(formData.source);

	return (
		<Content
			attachable={formHandler.for("source")}
			currentItems={currentItems}
			hasMoreOnServer={hasMoreOnServer}
			loadingState={getLoadingState()}
			onReloadList={reload}
			onRequestMoreItems={loadMoreHandler}
			ref={contentRef}
			sort={formData.sort}
			hasFilter={hasFilter}
			mobileSearchSettingsTarget={mobileSearchSettingsTarget}
			onClearFilters={clearHandler}
			sourceOptions={sourceOptions}
		>
			{shouldShowFilters && (
				<>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
					/>
					{formData.source === ContactSearchSource.Workspace ? (
						<>
							<SearchSettings.MultiSelect
								label={localize("_JobTitle")}
								attachable={formHandler.for("jobTitles")}
								options={jobTitleOptions}
								hasBusyIndicator={isLoadingFilters}
								placeholder={localize("_SelectJobTitle(s)")}
							/>
							<SearchSettings.MultiSelect
								label={localize("_Department")}
								attachable={formHandler.for("departments")}
								options={departmentOptions}
								hasBusyIndicator={isLoadingFilters}
								placeholder={localize("_SelectDepartment(s)")}
							/>
							<SearchSettings.MultiSelect
								label={localize("_OfficeLocation")}
								attachable={formHandler.for("officeLocations")}
								options={officeLocationOptions}
								hasBusyIndicator={isLoadingFilters}
								placeholder={localize("_SelectOfficeLocation(s)")}
							/>
						</>
					) : (
						<>
							<SearchSettings.TextInput
								label={localize("_JobTitle")}
								attachable={formHandler.for("jobTitle")}
								placeholder={localize("_EnterAJobTitle")}
							/>
							<SearchSettings.TextInput
								label={localize("_Department")}
								attachable={formHandler.for("department")}
								placeholder={localize("_EnterADepartment")}
							/>
							<SearchSettings.TextInput
								label={localize("_OfficeLocation")}
								attachable={formHandler.for("officeLocation")}
								placeholder={localize("_EnterAnOfficeLocation")}
							/>
						</>
					)}
				</>
			)}
		</Content>
	);
};
DefaultContent.displayName = "DefaultContent";

type GalContentProps = {
	searchTerm: VersionedValue<string>;
	defaultSource: ContactSearchSource.Workspace | ContactSearchSource.All;
	sourceOptions: readonly SelectOption<ContactSearchSource>[];
	mobileSearchSettingsTarget: HTMLDivElement | null;
	onSourceChange: (value: ContactSearchSource) => void;
};

const GalContent = (props: GalContentProps): ReactElement => {
	const {
		searchTerm,
		defaultSource,
		mobileSearchSettingsTarget,
		sourceOptions,
		onSourceChange
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [formData, setFormData] = useState<GalContactSearchFormData>({
		source: ContactSearchSource.Organisation
	});

	const onChange = useCallback(
		(value: GalContactSearchFormData): void => {
			setFormData(value);
			onSourceChange(value.source);
		},
		[onSourceChange]
	);

	const setValue: Dispatch<SetStateAction<GalContactSearchFormData>> = useSetStateAction(
		formData,
		onChange
	);

	const formHandler: IFormHandler<GalContactSearchFormData> = useFormHandler(
		formData,
		setValue,
		"NoValidation"
	);

	const contentRef: RefObject<ContentHandle | null> = useRef<ContentHandle | null>(null);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useGalContactsDataSource({
		searchTerm: searchTerm.value,
		desiredAmountOfItems
	});

	const clearFiltersHandler = useCallback(
		(): void => {
			onSourceChange(defaultSource);
		},
		[defaultSource, onSourceChange]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm.value);
			setDesiredAmountOfItems(PAGE_SIZE);

			contentRef
				.current
				?.resetScroll();
		},
		[searchTerm.value]
	);

	return (
		<Content
			attachable={formHandler.for("source")}
			sourceOptions={sourceOptions}
			currentItems={currentItems}
			hasMoreOnServer={hasMoreOnServer}
			loadingState={getLoadingState()}
			onClearFilters={clearFiltersHandler}
			onReloadList={reload}
			onRequestMoreItems={loadMoreHandler}
			ref={contentRef}
			sort={defaultSortValue}
			mobileSearchSettingsTarget={mobileSearchSettingsTarget}
			hasFilter
		/>
	);
};
GalContent.displayName = "GalContent";

type ContentHandle = {
	resetScroll: () => void;
};

type ContentProps = {
	attachable: IValueInputAttachable<ContactSearchSource>;
	currentItems: readonly ContactGlobalSearchResultItem[];
	loadingState: RemoteDataSourceState;
	hasMoreOnServer: boolean;
	sort: SortValue;
	ref: Ref<ContentHandle>;
	hasFilter: boolean;
	sourceOptions: readonly SelectOption<ContactSearchSource>[];
	mobileSearchSettingsTarget: HTMLDivElement | null;
	onRequestMoreItems: () => void;
	onReloadList: () => void;
	onClearFilters: () => void;
};

const Content = (props: PropsWithChildren<ContentProps>): ReactElement => {
	const {
		attachable,
		currentItems,
		loadingState,
		hasMoreOnServer,
		sort,
		ref,
		hasFilter,
		sourceOptions,
		mobileSearchSettingsTarget,
		onRequestMoreItems,
		onReloadList,
		onClearFilters,
		children
	} = props;

	const [contactDetails, setContactDetails] = useState<ContactDetailsState>({ isOpen: false });

	const attachableValue: ContactSearchSource = attachable
		.reducer
		.value;

	const openContactDetailsHandler = useCallback(
		(id: string, source: ContactSource): void => {
			if (source === ContactSource.Suggested) {
				throw new Error("Address book does not support suggested contacts");
			}

			setContactDetails({
				isOpen: true,
				contactId: id,
				source
			});
		},
		[]
	);

	const listHandle: RefObject<ScrollableVirtualListHandle<ListItem> | null> = useRef<ScrollableVirtualListHandle<ListItem>>(null);

	const itemsGroupBy = useMemo(
		(): ContactGroupingKind | undefined => {
			return getContactGroupingKind(
				attachableValue,
				sort
					.propertyName
			);
		},
		[
			sort.propertyName,
			attachableValue
		]
	);

	const list = useMemo(
		(): ListItem[] => {
			if (isNil(itemsGroupBy)) {
				return currentItems.map(
					(value: ContactGlobalSearchResultItem, index: number): ListItem => ({
						type: "item",
						value,
						index
					})
				);
			}

			const groups: readonly IContactGroup<ContactGlobalSearchResultItem>[] = groupAlreadySortedContacts(
				currentItems,
				itemsGroupBy,
				(x: ContactGlobalSearchResultItem): ContactListItem => x.contact
			);

			return groups.reduce(
				(acc: ListItem[], curr: IContactGroup<ContactGlobalSearchResultItem>): ListItem[] => {

					const latestItemIndex: number = findLastIndex(
						acc,
						(x) => x.type === "item"
					);

					const items: ListItem[] = curr.items
						.map((x, i) => ({
							type: "item",
							value: x,
							index: latestItemIndex === -1
								? i
								: i + latestItemIndex
						}));

					return [
						...acc,
						{
							type: "title",
							value: curr.title,
							id: curr.key
						},
						...items
					];
				},
				[]
			);
		},
		[currentItems, itemsGroupBy]
	);

	useImperativeHandle(
		ref,
		(): ContentHandle => ({
			resetScroll: (): void => {
				listHandle.current
					?.scrollToTop();
			}
		}),
		[]
	);

	const renderContent = (): ReactElement => {
		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || list.length > 0) {
			return (
				<ScrollableVirtualList
					items={list}
					estimatedHeightOfAnItem={71}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="contact-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={attachableValue !== ContactSearchSource.All && hasMoreOnServer}
								onRequestMoreItems={onRequestMoreItems}
							/>

							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: ListItem, index: number): ReactElement => {
						if (item.type === "title") {
							return (
								<h3
									className={
										classNames(
											styles.groupHeader,
											{
												[styles.groupHeader_noBorder]: index === 0
											}
										)
									}
								>
									{item.value === "" && isNotNil(itemsGroupBy)
										? groupByToEmptyGroupTitle[itemsGroupBy]
										: item.value
									}
								</h3>
							);
						}

						return (
							<ContactItemView
								navigationIndex={item.index}
								item={item.value}
								itemScope={contactSearchScope}
								onOpenContactDetails={openContactDetailsHandler}
							/>
						);
					}}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={onReloadList}
				height="fill"
			/>
		);
	};

	return (
		<>
			<ScopeLayout>
				<ScopeLayout.Settings>
					<SearchSettings
						menuTarget={mobileSearchSettingsTarget}
						onClear={onClearFilters}
						hasFilter={hasFilter}
					>
						{sourceOptions.length > 2 && (
							<SearchSettings.Select
								attachable={attachable}
								label={localize("_Source")}
								options={sourceOptions}
								isExpandedByDefault
							/>
						)}
						{children}
					</SearchSettings>
				</ScopeLayout.Settings>
				<ScopeLayout.Results>
					{renderContent()}
				</ScopeLayout.Results>
			</ScopeLayout>
			{contactDetails.isOpen && (
				<ContactDialog
					source={contactDetails.source}
					initialView={{
						kind: "details",
						contactId: contactDetails.contactId
					}}
					onClose={() => setContactDetails({ isOpen: false })}
				/>
			)}
		</>
	);
};
Content.displayName = "Content";

export const PAGE_SIZE = 20;
const DEFAULT_SIZE_FOR_ALL_SOURCE = 50;

const configurableContactSourcesToContactSearchSourceItem: { [k in ConfigurableAddressBookAppContactSource]: SelectOption<ContactSearchSource>; } = {
	[ConfigurableAddressBookAppContactSource.Gal]: {
		text: localize("_Organisation(Contacts)"),
		value: ContactSearchSource.Organisation
	},
	[ConfigurableAddressBookAppContactSource.Personal]: {
		text: localize("_Personal(Contacts)"),
		value: ContactSearchSource.Personal
	}
};

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<ContactGlobalSearchResultItem>;
};

type DefaultContactSearchSource =
	Exclude<ContactSearchSource, ContactSearchSource.Organisation>;

type ContactSearchFormData = {
	sort: SortValue;
	source: DefaultContactSearchSource;
	jobTitles: readonly string[];
	departments: readonly string[];
	officeLocations: readonly string[];
	jobTitle: VersionedValue<string>;
	department: VersionedValue<string>;
	officeLocation: VersionedValue<string>;
};

type GalContactSearchFormData = {
	source: ContactSearchSource.Organisation;
};

type FilterModel = Omit<ContactSearchFormData, "sort">;

function getDefaultFilters(
	defaultSource: ContactSearchSource.Workspace | ContactSearchSource.All
): FilterModel {
	return {
		source: defaultSource,
		jobTitles: EMPTY_ARRAY,
		departments: EMPTY_ARRAY,
		officeLocations: EMPTY_ARRAY,
		department: {
			value: "",
			version: newGuid()
		},
		jobTitle: {
			value: "",
			version: newGuid()
		},
		officeLocation: {
			value: "",
			version: newGuid()
		}
	};
};

type FilterContext = {
	source: Exclude<ContactSearchSource, ContactSearchSource.Organisation>;
	jobTitles: readonly string[];
	departments: readonly string[];
	officeLocations: readonly string[];
	jobTitle: string;
	department: string;
	officeLocation: string;
};

const useDataSource = createRemoteDataSource<
	ContactGlobalSearchResultItem,
	FilterContext,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
	})
	.getHook({
		performRequest:
			async (options): Promise<LazyLoadPageResult<ContactGlobalSearchResultItem>> => {
				if (isNilOrWhiteSpace(options.searchTerm)) {
					return {items: [], hasMore: false};
				}

				const {
					searchTerm,
					filterState,
					skip,
					amount,
					sorting
				} = options;

				const sortBy: UserSortBy = filterState.source === ContactSearchSource.All
					? UserSortBy.Default
					: convertSort(
						ensureNotNil(sorting)
					);

				let request: ContactExtendedSearchRequestModel = {
					searchTerm: searchTerm,
					source: filterState.source,
					departments: [],
					jobTitles: [],
					officeLocations: [],
					skip: skip,
					take: amount,
					sortBy
				};

				if (filterState.source === ContactSearchSource.Personal) {
					request = {
						...request,
						departments: filterState.department !== ""
							? [filterState.department]
							: [],
						jobTitles: filterState.jobTitle !== ""
							? [filterState.jobTitle]
							: [],
						officeLocations: filterState.officeLocation !== ""
							? [filterState.officeLocation]
							: []
					};
				}

				if (filterState.source === ContactSearchSource.Workspace) {
					request = {
						...request,
						departments: filterState.departments,
						jobTitles: filterState.jobTitles,
						officeLocations: filterState.officeLocations
					};
				}

				const { lazyLoadPageResult } = await searchContacts(
					request,
					options.signal
				);

				return lazyLoadPageResult;
			},
	});

const useGalContactsDataSource = createRemoteDataSource<
	ContactGlobalSearchResultItem
>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipToken,
	})
	.getHook(
		{
			performRequest: async (options): Promise<SkipTokenPageResult<ContactGlobalSearchResultItem>> => {
				if (isNilOrWhiteSpace(options.searchTerm)) {
					return {items: [], skipToken: null };
				}

				const {
					searchTerm,
					amount,
					skipToken,
					signal
				} = options;

				const { skipTokenPageResult } = await searchContacts(
					{
						source: ContactSearchSource.Organisation,
						searchTerm,
						skipToken,
						take: amount
					},
					signal
				);

				return skipTokenPageResult;
			}
		}
	);

type ListItem = {
	type: "title";
	value: string;
	id: string;
} | {
	type: "item";
	value: {
		kind: GlobalSearchResultItemKind.Contact;
		hitHighlights: readonly HighlightItem[];
		contact: ContactListItem;
		contactSource: ContactSource;
	};
	index: number;
};

function getUniqueKey(item: ListItem): string {
	if (item.type === "title") {
		return `title_${item.id}`;
	}

	return `${item.value.contact.id}`;
}

const defaultSortValue: SortValue = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "contact.firstName"
};

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: localize("_FirstName"),
		value: defaultSortValue,
		valueTrackingId: "contact-firstname"
	},
	{
		text: localize("_LastName"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "contact.lastName"
		},
		valueTrackingId: "contact-lastname"
	},
	{
		text: localize("_JobTitle"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "contact.jobTitle"
		},
		valueTrackingId: "contact-job-title"
	},
	{
		text: localize("_Department"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "contact.department"
		},
		valueTrackingId: "contact-department"
	},
	{
		text: localize("_Location"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "contact.officeLocation"
		},
		valueTrackingId: "contact-office-location"
	}
];

function convertToSortedOptions(values: readonly string[]): SelectOption<string>[] {
	return [...values].sort()
		.map((value) => {
			return {
				text: value,
				value
			};
		});
}

function convertSort(
	sortingState: DataSourceSortableState<ContactGlobalSearchResultItem>
): UserSortBy {
	if (!sortingState) {
		return UserSortBy.Default;
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	switch(sortingState.propertyName) {
		case "contact.firstName":
			return UserSortBy.FirstName;
		case "contact.lastName":
			return UserSortBy.LastName;
		case "contact.jobTitle":
			return UserSortBy.JobTitle;
		case "contact.department":
			return UserSortBy.Department;
		case "contact.officeLocation":
			return UserSortBy.OfficeLocation;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}
}

function getContactGroupingKind(
	source: ContactSearchSource,
	sortPropertyName: PropertyPath<ContactGlobalSearchResultItem>
): ContactGroupingKind | undefined{
	if (source === ContactSearchSource.All) {
		return undefined;
	}

	if (source === ContactSearchSource.Organisation) {
		return ContactGroupingKind.FirstLetterDisplayName;
	}

	switch (sortPropertyName) {
		case "contact.firstName":
			return ContactGroupingKind.FirstLetterFirstName;
		case "contact.lastName":
			return ContactGroupingKind.FirstLetterLastName;
		case "contact.jobTitle":
			return ContactGroupingKind.JobTitle;
		case "contact.department":
			return ContactGroupingKind.Department;
		case "contact.officeLocation":
			return ContactGroupingKind.OfficeLocation;
		default:
			return undefined;
	}
}

const CONTACT_SOURCES_WITH_FILTERS: ContactSearchSource[] = [
	ContactSearchSource.Personal,
	ContactSearchSource.Workspace
];

type ContactDetailsState = {
	isOpen: true;
	source: AddressBookContactSource;
	contactId: string;
} | {
	isOpen: false;
};

const contactSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Contacts
};